import produce from "immer";

import {
  type ProgramTheme,
  type WithOrganization,
  type VerificationResponse,
  type ViewModel,
  type InactiveMilitaryPersonalInfoViewModel,
  type PersonalInfoResponse,
  type VerificationStep,
  type PersonalInfoViewModel,
  type DocUploadViewModel,
  type Locale,
  type Country,
  type FieldValidationErrors,
  type ErrorId,
  type FieldId,
} from "../types/types";
import { hooks } from "../hooks";
import { VerificationStepsEnum, isSuccessResponse } from "../types/runtimeTypes";
import { getEmptyViewModel } from "../ServerApi/VerificationRequestGetters";
import {
  MAX_DOC_UPLOAD_DOCS_ALLOWED,
  QUERY_STRING_EXTERNAL_USER_ID,
  UPLOAD_FILE_PREFIX,
} from "../../constants";
import { deepClone } from "../utils/objects";
import { getQueryOrOption } from "../ServerApi/TestingRouteOverrides";
import { initialErrorStructure } from "../types/empties";
import { getQueryParam } from "../utils/routing/Url";

/**
 * @todo needs tests
 * @private
 */
export const handleSubmitResponse = (
  verificationResponse: VerificationResponse,
  viewModel: ViewModel,
  programTheme: ProgramTheme,
): ViewModel => {
  const { currentStep } = verificationResponse;

  if (isSuccessResponse(verificationResponse)) {
    hooks.emit("ON_VERIFICATION_SUCCESS", verificationResponse);
  }

  // TODO: Find a better way to persist data between military steps.
  // Ensure the new viewModel for military steps persists data from the previous military step.
  let newViewModel: ViewModel;

  if (
    currentStep === VerificationStepsEnum.collectActiveMilitaryPersonalInfo &&
    !programTheme.transitionalMilitaryValidityDays
  ) {
    newViewModel = { ...viewModel, dischargeDate: "" } as InactiveMilitaryPersonalInfoViewModel;
    delete (newViewModel as InactiveMilitaryPersonalInfoViewModel).dischargeDate;
  } else if (currentStep === VerificationStepsEnum.collectInactiveMilitaryPersonalInfo) {
    if (!(viewModel as InactiveMilitaryPersonalInfoViewModel).dischargeDate) {
      newViewModel = { ...viewModel, dischargeDate: "" } as InactiveMilitaryPersonalInfoViewModel;
    } else {
      newViewModel = viewModel;
    }
  } else if (hasFailedInstantMatch(verificationResponse as PersonalInfoResponse)) {
    newViewModel = viewModel;
  } else {
    // If not military, and not on the confirm info 'step', get an empty viewModel
    const { locale } = verificationResponse;
    newViewModel = initViewModel({
      locale,
      previousViewModel: viewModel,
      currentStep,
      externalUserId: getQueryOrOption(QUERY_STRING_EXTERNAL_USER_ID),
    });
  }
  return newViewModel;
};

/**
 * @private
 */
export const hasFailedInstantMatch = (verificationResponse: PersonalInfoResponse): boolean => {
  if (verificationResponse.instantMatchAttempts && verificationResponse.instantMatchAttempts > 0) {
    return true;
  }
  return false;
};

/**
 * @private
 */
export const initViewModel = ({
  previousViewModel,
  currentStep,
  locale,
  fingerprint,
  country,
  externalUserId,
}: {
  previousViewModel: ViewModel;
  currentStep: VerificationStep;
  locale: Locale;
  fingerprint?: string;
  country?: Country;
  externalUserId?: string;
}): ViewModel => {
  const viewModel = getEmptyViewModel(currentStep);

  // Attempt to bring over any previousViewModel values that apply to the new viewModel
  if (previousViewModel) {
    Object.getOwnPropertyNames(viewModel).forEach((key) => {
      if (previousViewModel.hasOwnProperty(key)) {
        viewModel[key] = previousViewModel[key];
      }
    });
  }

  if (viewModel) {
    if (fingerprint) {
      (viewModel as PersonalInfoViewModel).deviceFingerprintHash = fingerprint;
    }
    if (locale) {
      (viewModel as PersonalInfoViewModel).localeChoice = {
        value: locale || "en-US",
        label: "",
      };
    }
    if (country && "country" in viewModel && viewModel.country === undefined) {
      viewModel.country = country;
    }
    if (country && "countryChoice" in viewModel && viewModel.countryChoice === undefined) {
      viewModel.countryChoice = {
        value: country,
        label: "",
      };
    }
    if (externalUserId) {
      (viewModel as PersonalInfoViewModel).externalUserId = externalUserId;
    }
  }

  return viewModel;
};

export const clearUploadedFiles = (
  viewModel: DocUploadViewModel,
): DocUploadViewModel | undefined => {
  if (viewModel) {
    return produce(viewModel, (draft) => {
      for (let i = 1; i <= MAX_DOC_UPLOAD_DOCS_ALLOWED; i += 1) {
        draft[`${UPLOAD_FILE_PREFIX}${i}`] = undefined;
      }
    });
  }
  return undefined;
};

export const collectThreatMetrixProfile = async (verificationId: string, programId: string) => {
  const programIdToOrgId: Record<string, string> = {
    "63a35d55add1107a3bc5edb6": "4a6mj7xe",
    "60870a785f8f9c61aca0f11f": "4a6mj7xe",
    "63fd266996552d469aea40e1": "4a6mj7xe",
    "686eaf99d55e893b64da72b5": "41vh0yph",
    "6835da88629e1c44b45c049b": "41vh0yph",
    "5e94c9ee0ec57b1ba907a4fa": "41vh0yph",
    "5d83fe3def70971fc2f6b3c7": "41vh0yph",
    "5be5cce4895f11144700bf66": "41vh0yph",
    "68af74c746f35c0c33b11f7e": "41vh0yph",
    "5b609e64944963167d6cde7b": "41vh0yph",
    "68371bf424e7d8407ba61551": "41vh0yph",
    "5f3d73325c81e518c080bf01": "41vh0yph",
    "5bfed239e69819126c9e2300": "6s33lpne",
    "5d9e307c45b7b120b75cdc7a": "6s33lpne",
    "633f45d7295c0551ab43b87a": "6s33lpne",
    "6283e0f3401a404cb53704e3": "6s33lpne",
    "5bfed238932f4b1330576e4d": "6s33lpne",
    "61ccc40cbfb19932e5e7f220": "41vh0yph",
    "6058e5f822ca3400652e304b": "41vh0yph",
    "674de64fa3df0f5354b4cd93": "41vh0yph",
    "6008b2d67ea692195efe4047": "41vh0yph",
    "6008b36aa65a69195e48bef9": "41vh0yph",
    "6019d9fbc66edf1a0fd19b8b": "41vh0yph",
    "6054c8d3bbcc170065c88b37": "41vh0yph",
    "6054c8bdbbcc170065c8892c": "41vh0yph",
    "61ca3c1db1fdc21390a239b3": "38grtc73",
    "69173080e7d00d521ce139c0": "38grtc73",
    "622276a7c2d4683557eedb56": "38grtc73",
    "616626fbfe36cb56e69984e3": "38grtc73",
    "69252575f84c722495040e20": "38grtc73",
    "67d8c978ca73cd576d8c35f3": "6s33lpne",
    "6818c2f8521eb235469c3058": "6s33lpne",
    "6706daa505575b2c7476b195": "6s33lpne",
    "65494402a9f6da45f37f1a83": "6s33lpne",
    "654d269923374a743123f178": "6s33lpne",
    "644ab175f5c88f24d4850dc7": "6s33lpne",
    "64832e9d51d7ee3f0a813a19": "6s33lpne",
    "68014714e9c4e6582cf82011": "6gepep58",
    "680145d9e9c4e6582cf8126b": "6gepep58",
    "68014636e9c4e6582cf816ed": "6gepep58",
    "6879467f9a02bf76c7d1bf7a": "6gepep58",
    "68014601e9c4e6582cf813ef": "6gepep58",
    "68014598e9c4e6582cf80f5d": "6gepep58",
    "67b67bb3855c8b2c02969a4e": "6s33lpne",
    "67a3f0217c740a497f6b4af3": "6s33lpne",
    "691e317dedbe840cda54c958": "6s33lpne",
    "67c8c14f5f17a83b745e3f82": "6s33lpne",
    "690415d58971e73ca187d8c9": "bzi5zrdy",
    "67eb16281bc6f4327a3e8364": "bzi5zrdy",
    "68f7b326d3394435ecf77cab": "bzi5zrdy",
    "68d47554aa292d20b9bec8f7": "bzi5zrdy",
    "69b1aafc617d5165c65f8173": "bzi5zrdy",
  };

  let orgId = programIdToOrgId[programId];

  if (!orgId) {
    orgId = "cnl2my47";
  }

  import(/* webpackChunkName: "Tmetrix" */ "../ThreatMetrix/index")
    .then(({ ThreatMetrix }) => {
      if (
        ThreatMetrix !== undefined &&
        ThreatMetrix.hasOwnProperty("profile") &&
        typeof ThreatMetrix.profile === "function"
      ) {
        const profilingDomain = "content.sheerid.com";
        ThreatMetrix.profile(profilingDomain, orgId, verificationId);
      }
    })
    .catch(() => {
      // Do nothing - API call will handle this
    });
};

export const enhanceOrganizationFromTheme = (viewModel: ViewModel, programTheme: ProgramTheme) => {
  if (
    viewModel &&
    viewModel.hasOwnProperty("organization") &&
    programTheme.config.orgRemoteSource
  ) {
    const cloned: ViewModel = deepClone(viewModel);
    const { organization } = cloned as WithOrganization;
    organization.source = programTheme.config.orgRemoteSource;
    return cloned;
  }
  return viewModel;
};

export const determineCountry = (
  verificationResponse: VerificationResponse,
  countries: Country[],
): Country => {
  if (verificationResponse?.country) return verificationResponse.country;

  const defaultCountry = "US";
  if (!Array.isArray(countries) || countries.length === 0) return defaultCountry;
  if (countries.length === 1) return countries[0];

  const countryQueryParam = getQueryParam("country")?.toUpperCase();
  return countries.find((c) => c === countryQueryParam);
};

export const fieldIdErrorMap = new Map<ErrorId, FieldId>([
  ["invalidFirstName", "firstName"],
  ["invalidLastName", "lastName"],
  ["invalidMemberId", "memberId"],
  ["invalidEmail", "email"],
  ["invalidPhoneNumber", "phoneNumber"],
  ["invalidBirthDate", "birthDate"],
  ["invalidOrganization", "organization"],
  ["invalidPostalCode", "postalCode"],
  ["invalidAddress", "address1"],
  ["invalidCity", "city"],
  ["invalidCountry", "country"],
  ["invalidState", "state"],
  ["invalidActiveDutyStartDate", "activeDutyStartDate"],
  ["invalidDischargeDate", "dischargeDate"],
  ["invalidStatus", "status"],
  ["invalidStatuses", "statuses"],
  ["invalidSocialSecurityNumber", "socialSecurityNumber"],
  ["invalidDriverLicenseNumber", "driverLicenseNumber"],
  ["invalidEbtCardNumber", "ebtCardNumber"],
  ["invalidOverrideCode", "overrideCode"],
  ["invalidCvecNumber", "cvecNumber"],
  ["invalidCaptchaToken", "captchaToken"],
]);

export const getServerFieldValidationErrors = (
  serverErrorIds: ErrorId[],
): FieldValidationErrors => {
  let fieldValidationErrors = deepClone(initialErrorStructure);

  serverErrorIds.forEach((serverErrorId: ErrorId) => {
    const fieldId = fieldIdErrorMap.get(serverErrorId);
    if (fieldId) {
      fieldValidationErrors = { ...fieldValidationErrors, [fieldId]: serverErrorId };
    }
  });

  return fieldValidationErrors;
};
